import { WebMercatorViewport, type MapViewState } from "deck.gl";

export type LngLat = [number, number];
export type LngLatAltitude = [number, number, number];

export interface RouteArcPath {
  origin: LngLat;
  destination: LngLat;
  path: LngLatAltitude[];
  timestamps: number[];
}

export interface ViewportSize {
  width: number;
  height: number;
}

type WorldPoint = [number, number];

const EARTH_RADIUS_METERS = 6_371_008.8;
const WORLD_SIZE = 512;
const ARC_HEIGHT_RATIO = 0.22;
const ARC_SEGMENT_COUNT = 64;
const VIEW_PITCH = 45;
const VIEW_MIN_ZOOM = 2;
const VIEW_MAX_ZOOM = 12;
const VIEW_PADDING_RATIO = 0.15;

export function getRouteArcPosition(origin: LngLat, destination: LngLat, fraction: number): LngLatAltitude {
  return interpolateRouteArc(origin, destination, fraction, getArcPeakHeightMeters(origin, destination));
}

export function createRouteArcPath(origin: LngLat, destination: LngLat): RouteArcPath {
  const peakHeightMeters = getArcPeakHeightMeters(origin, destination);
  const timestamps = Array.from({ length: ARC_SEGMENT_COUNT + 1 }, (_, index) => index / ARC_SEGMENT_COUNT);

  return {
    origin,
    destination,
    path: timestamps.map((fraction) => interpolateRouteArc(origin, destination, fraction, peakHeightMeters)),
    timestamps,
  };
}

export function getRouteViewState(
  origin: LngLat,
  destination: LngLat,
  viewport: ViewportSize,
  current: LngLat | null = null,
): MapViewState {
  const points = [origin, destination, ...(current ? [current] : [])];
  const longitudes = points.map(([longitude]) => longitude);
  const latitudes = points.map(([, latitude]) => latitude);
  const { longitude, latitude, zoom } = new WebMercatorViewport(viewport).fitBounds(
    [
      [Math.min(...longitudes), Math.min(...latitudes)],
      [Math.max(...longitudes), Math.max(...latitudes)],
    ],
    {
      maxZoom: VIEW_MAX_ZOOM,
      padding: {
        top: viewport.height * VIEW_PADDING_RATIO,
        bottom: viewport.height * VIEW_PADDING_RATIO,
        left: viewport.width * VIEW_PADDING_RATIO,
        right: viewport.width * VIEW_PADDING_RATIO,
      },
    },
  );

  return {
    longitude,
    latitude,
    zoom: Math.max(VIEW_MIN_ZOOM, zoom),
    pitch: VIEW_PITCH,
    bearing: 0,
  };
}

function interpolateRouteArc(
  origin: LngLat,
  destination: LngLat,
  fraction: number,
  peakHeightMeters: number,
): LngLatAltitude {
  const [originX, originY] = projectToWorld(origin);
  const [destinationX, destinationY] = projectToWorld(destination);
  const [longitude, latitude] = unprojectFromWorld([
    originX + (destinationX - originX) * fraction,
    originY + (destinationY - originY) * fraction,
  ]);

  return [longitude, latitude, peakHeightMeters * Math.sin(Math.PI * fraction)];
}

function getArcPeakHeightMeters(origin: LngLat, destination: LngLat): number {
  return getDistanceMeters(origin, destination) * ARC_HEIGHT_RATIO;
}

function getDistanceMeters(
  [originLongitude, originLatitude]: LngLat,
  [destinationLongitude, destinationLatitude]: LngLat,
): number {
  const latitudeDelta = toRadians(destinationLatitude - originLatitude);
  const longitudeDelta = toRadians(destinationLongitude - originLongitude);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(originLatitude)) * Math.cos(toRadians(destinationLatitude)) * Math.sin(longitudeDelta / 2) ** 2;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

function projectToWorld([longitude, latitude]: LngLat): WorldPoint {
  return [
    (WORLD_SIZE * (longitude + 180)) / 360,
    (WORLD_SIZE * (Math.PI - Math.log(Math.tan(Math.PI / 4 + toRadians(latitude) / 2)))) / (2 * Math.PI),
  ];
}

function unprojectFromWorld([x, y]: WorldPoint): LngLat {
  return [
    (x / WORLD_SIZE) * 360 - 180,
    toDegrees(2 * Math.atan(Math.exp(Math.PI - (2 * Math.PI * y) / WORLD_SIZE))) - 90,
  ];
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}
